import { ChartBar as BarChart3, Target, TrendingUp, Eye } from 'lucide-react';
import type { TabMetricsOutcomes } from '../../../../types/craft';
import FormattedText from '../../../common/FormattedText';

interface Props {
  data: TabMetricsOutcomes;
}

export default function MetricsOutcomesTab({ data }: Props) {
  if (!data?.metrics?.length && !data?.observable_outcomes?.length) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-app-muted">No data available yet.</p>
        <p className="text-xs text-app-muted mt-1">Generate this module to populate content.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {data.metrics?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-app-focus" />
            <h4 className="text-sm font-semibold text-app-ink">Mastery Metrics</h4>
            <span className="text-xs text-app-muted">{data.metrics.length} metrics</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.metrics.map((m, i) => (
              <div key={i} className="bg-app-surface border border-app-line rounded-lg p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-4 h-4 text-app-focus" />
                  <h5 className="text-sm font-semibold text-app-ink">{m.metric}</h5>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  {m.baseline && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-app-muted mb-0.5">Baseline</p>
                      <p className="text-sm font-medium text-app-ink">{m.baseline}</p>
                    </div>
                  )}
                  {m.target && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-app-muted mb-0.5">Target</p>
                      <p className="text-sm font-medium text-app-positive">{m.target}</p>
                    </div>
                  )}
                </div>

                {m.how_to_measure && (
                  <p className="text-xs text-app-muted mb-2"><span className="font-medium">How to measure:</span> {m.how_to_measure}</p>
                )}

                {m.leading_indicators?.length > 0 && (
                  <div className="bg-app-focus/10 border border-app-focus/30 rounded-lg px-3 py-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-app-focus flex items-center gap-1">
                      <TrendingUp className="w-2.5 h-2.5" /> Leading Indicators
                    </span>
                    <ul className="mt-1 space-y-1">
                      {m.leading_indicators.map((ind, j) => (
                        <li key={j} className="flex items-start gap-1.5 text-xs text-app-focus">
                          <span className="w-1 h-1 rounded-full bg-app-focus mt-1.5 flex-shrink-0" />
                          {ind}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.observable_outcomes?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Eye className="w-4 h-4 text-app-positive" />
            <h4 className="text-sm font-semibold text-app-ink">Observable Outcomes</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.observable_outcomes.map((o, i) => (
              <div key={i} className="flex items-start gap-2 bg-app-positive/10 border border-app-positive rounded-lg p-3">
                <span className="w-1.5 h-1.5 rounded-full bg-app-positive mt-1.5 flex-shrink-0" />
                <p className="text-xs text-app-positive">{o}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.assessment_criteria && (
        <div className="bg-app-canvas border border-app-line rounded-lg p-5">
          <h4 className="text-sm font-semibold text-app-ink mb-2">Assessment Criteria</h4>
          <FormattedText text={data.assessment_criteria} className="text-sm text-app-ink" />
        </div>
      )}
    </div>
  );
}
